//--------------- CARD + MODAL DE CONFIGURAÇÃO DO AMAZON SES ---------------//
// E-mail transacional por empresa (2º provedor, ao lado do Resend). Access key
// e secret ficam cifrados no backend; aqui só aparece o final da access key.
// O nó "Email (AWS SES)" das automações usa essa configuração.
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Cloud, Settings2, CheckCircle2, AlertTriangle, Loader2, Copy, RefreshCw,
  Send, ShieldCheck, Globe, X, Gauge,
} from "lucide-react";
import emailSesConfigService from "../services/emailSesConfigService";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const REGIOES = [
  ["us-east-1", "US East (N. Virginia)"],
  ["us-east-2", "US East (Ohio)"],
  ["us-west-2", "US West (Oregon)"],
  ["sa-east-1", "South America (São Paulo)"],
  ["eu-west-1", "Europe (Ireland)"],
  ["eu-central-1", "Europe (Frankfurt)"],
  ["eu-west-2", "Europe (London)"],
  ["ca-central-1", "Canada (Central)"],
  ["ap-southeast-1", "Asia Pacific (Singapore)"],
  ["ap-southeast-2", "Asia Pacific (Sydney)"],
  ["ap-northeast-1", "Asia Pacific (Tokyo)"],
  ["ap-south-1", "Asia Pacific (Mumbai)"],
];

function StatusPill({ ok, label, warn }) {
  const cls = ok
    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
    : warn
      ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
      : "bg-red-500/10 border-red-500/30 text-red-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${cls}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {label}
    </span>
  );
}

export default function SesCard() {
  const [dados, setDados] = useState(null); // { config, webhook_url }
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState(false);

  const carregar = () => {
    setCarregando(true);
    return emailSesConfigService.obter()
      .then((r) => setDados(r.data))
      .catch(() => toast.error("Erro ao carregar configuração do Amazon SES"))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { carregar(); }, []);

  const cfg = dados?.config;
  const configurado = Boolean(cfg?.tem_credenciais && cfg?.remetente_email && cfg?.ativo);

  return (
    <>
      <div className="mb-8">
        <div className="surface-card p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 grid place-items-center shrink-0">
            <Cloud className="w-5 h-5 text-amber-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-white">Amazon SES</span>
              {carregando ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-ink-4" />
              ) : (
                <StatusPill ok={configurado} warn label={configurado ? "configurado" : "não configurado"} />
              )}
              {!carregando && cfg?.tem_credenciais && (
                <StatusPill ok={cfg.sns_confirmado} warn label={cfg.sns_confirmado ? "eventos ligados" : "eventos pendentes"} />
              )}
            </div>
            <div className="text-sm text-ink-3 mt-0.5 truncate">
              {configurado
                ? <>Remetente <b className="text-ink-2">{cfg.remetente_nome ? `${cfg.remetente_nome} <${cfg.remetente_email}>` : cfg.remetente_email}</b> · {cfg.regiao}{cfg.access_key_hint ? ` · chave …${cfg.access_key_hint}` : ""}</>
                : "Configure as credenciais AWS e o remetente pra usar o nó Email (AWS SES) nas automações."}
            </div>
          </div>
          <button
            onClick={() => setAberto(true)}
            className="btn-primary !py-2 !px-4 inline-flex items-center gap-2 shrink-0"
          >
            <Settings2 className="w-4 h-4" /> {configurado ? "Editar" : "Configurar"}
          </button>
        </div>
      </div>

      {aberto && (
        <ModalSes
          dados={dados}
          onClose={() => setAberto(false)}
          onSaved={(novo) => { setDados(novo); }}
        />
      )}
    </>
  );
}

function ModalSes({ dados, onClose, onSaved }) {
  const cfg = dados?.config || {};
  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [regiao, setRegiao] = useState(cfg.regiao || "us-east-1");
  const [remetenteNome, setRemetenteNome] = useState(cfg.remetente_nome || "");
  const [remetenteEmail, setRemetenteEmail] = useState(cfg.remetente_email || "");
  const [replyTo, setReplyTo] = useState(cfg.reply_to || "");
  const [ativo, setAtivo] = useState(cfg.ativo !== false);
  const [topicArn, setTopicArn] = useState(cfg.sns_topic_arn || "");
  const [configSet, setConfigSet] = useState(cfg.configuration_set || "");
  const [salvando, setSalvando] = useState(false);
  const [conta, setConta] = useState(null);
  const [identidades, setIdentidades] = useState(null);
  const [carregandoAws, setCarregandoAws] = useState(false);
  const [provisionando, setProvisionando] = useState(false);
  const [testePara, setTestePara] = useState("");
  const [testando, setTestando] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(dados?.webhook_url || null);
  const [temCred, setTemCred] = useState(Boolean(cfg.tem_credenciais));
  const [snsConfirmado, setSnsConfirmado] = useState(Boolean(cfg.sns_confirmado));

  const carregarAws = async () => {
    setCarregandoAws(true);
    try {
      const [c, i] = await Promise.all([emailSesConfigService.conta(), emailSesConfigService.identidades()]);
      setConta(c.data || null);
      setIdentidades(i.data?.identidades || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao consultar a conta AWS");
      setConta(null); setIdentidades([]);
    } finally {
      setCarregandoAws(false);
    }
  };

  useEffect(() => { if (temCred) carregarAws(); /* eslint-disable-line */ }, []);

  const salvar = async () => {
    if (remetenteEmail && !EMAIL_RE.test(remetenteEmail)) { toast.error("E-mail do remetente inválido"); return false; }
    if (replyTo && !EMAIL_RE.test(replyTo)) { toast.error("Reply-to inválido"); return false; }
    if ((accessKeyId.trim() && !secretKey.trim()) || (!accessKeyId.trim() && secretKey.trim())) {
      toast.error("Informe Access Key ID e Secret Access Key juntos"); return false;
    }
    setSalvando(true);
    try {
      const payload = {
        regiao, remetente_nome: remetenteNome, remetente_email: remetenteEmail, reply_to: replyTo, ativo,
        configuration_set: configSet, sns_topic_arn: topicArn,
      };
      if (accessKeyId.trim()) payload.access_key_id = accessKeyId.trim();
      if (secretKey.trim()) payload.secret_access_key = secretKey.trim();
      const r = await emailSesConfigService.salvar(payload);
      onSaved?.(r.data);
      setWebhookUrl(r.data?.webhook_url || webhookUrl);
      const tinha = temCred;
      setTemCred(Boolean(r.data?.config?.tem_credenciais));
      setAccessKeyId(""); setSecretKey("");
      toast.success("Configuração do Amazon SES salva");
      if (r.data?.config?.tem_credenciais && (!tinha || payload.access_key_id)) carregarAws();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao salvar");
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const provisionar = async () => {
    setProvisionando(true);
    try {
      if (accessKeyId.trim() || !temCred) {
        const ok = await salvar();
        if (!ok) return;
      }
      const r = await emailSesConfigService.provisionar();
      setTopicArn(r.data?.topic_arn || topicArn);
      setConfigSet(r.data?.configuration_set || configSet);
      setSnsConfirmado(Boolean(r.data?.config?.sns_confirmado));
      onSaved?.({ config: r.data?.config, webhook_url: r.data?.webhook_url });
      if (r.data?.subscription === "confirmada") toast.success("Eventos já estavam ligados (subscription confirmada)");
      else toast.info("Tópico, configuration set e subscription criados. O SNS confirma o endpoint em segundos — recarregue pra ver o status.", { autoClose: 10000 });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao provisionar eventos", { autoClose: 10000 });
    } finally {
      setProvisionando(false);
    }
  };

  const testar = async () => {
    if (!EMAIL_RE.test(testePara)) { toast.error("Informe um e-mail válido"); return; }
    setTestando(true);
    try {
      if (accessKeyId.trim() || remetenteEmail !== (cfg.remetente_email || "") || regiao !== (cfg.regiao || "us-east-1")) {
        const ok = await salvar();
        if (!ok) return;
      }
      await emailSesConfigService.testar(testePara.trim());
      toast.success(`E-mail de teste enviado pra ${testePara.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Falha no envio de teste", { autoClose: 10000 });
    } finally {
      setTestando(false);
    }
  };

  const copiar = (txt) => {
    navigator.clipboard?.writeText(txt).then(() => toast.info("Copiado"));
  };

  const dominioRemetente = remetenteEmail.split("@")[1]?.toLowerCase();
  const remetenteVerificado = identidades?.some((i) => i.verificado && (
    (i.tipo === "dominio" && i.nome.toLowerCase() === dominioRemetente) ||
    (i.tipo === "email" && i.nome.toLowerCase() === remetenteEmail.toLowerCase())
  ));

  const inputCls = "w-full bg-surface-1 border border-white/[0.06] focus:border-emerald-500/40 rounded-lg px-3 py-2 text-sm outline-none";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-base border border-white/[0.06] rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-white/[0.04] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 grid place-items-center">
              <Cloud className="w-4 h-4 text-amber-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Amazon SES — e-mail transacional</div>
              <div className="text-[11px] text-ink-4">Credenciais ficam cifradas no servidor. Só o final da access key é exibido.</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-1 text-ink-4 hover:text-ink-2"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* CREDENCIAIS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 text-[11px] text-ink-4">
              {temCred
                ? <>Credenciais salvas (access key …{cfg.access_key_hint}). Cole um novo par só se quiser trocar.</>
                : <>Crie um usuário IAM com permissão de SES (e SNS, se quiser ligar os eventos por aqui) e gere uma access key.</>}
            </div>
            <div>
              <label className="block text-xs text-ink-2 font-medium mb-1">Access Key ID</label>
              <input type="password" value={accessKeyId} onChange={(e) => setAccessKeyId(e.target.value)}
                placeholder={temCred ? "••••••••••••" : "AKIA..."} className={inputCls} autoComplete="off" />
            </div>
            <div>
              <label className="block text-xs text-ink-2 font-medium mb-1">Secret Access Key</label>
              <input type="password" value={secretKey} onChange={(e) => setSecretKey(e.target.value)}
                placeholder={temCred ? "••••••••••••" : "wJalr..."} className={inputCls} autoComplete="off" />
            </div>
            <div>
              <label className="block text-xs text-ink-2 font-medium mb-1">Região</label>
              <select value={regiao} onChange={(e) => setRegiao(e.target.value)} className={inputCls}>
                {REGIOES.map(([v, l]) => <option key={v} value={v}>{v} — {l}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-2 mt-5 cursor-pointer">
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} className="accent-emerald-500" />
              Envio de e-mail ativo
            </label>
          </div>

          {/* REMETENTE */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-2 font-medium mb-1">Nome do remetente</label>
              <input value={remetenteNome} onChange={(e) => setRemetenteNome(e.target.value)} placeholder="Metriqa" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-ink-2 font-medium mb-1">E-mail do remetente</label>
              <input value={remetenteEmail} onChange={(e) => setRemetenteEmail(e.target.value)} placeholder="contato@seudominio.com.br" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-ink-2 font-medium mb-1">Reply-to (opcional)</label>
              <input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="suporte@seudominio.com.br" className={inputCls} />
            </div>
          </div>

          {/* CONTA + IDENTIDADES */}
          <div className="p-3 rounded-lg bg-surface-1 border border-white/[0.06]">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-ink-2 font-medium inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> Conta e identidades no SES</div>
              <button onClick={carregarAws} disabled={!temCred || carregandoAws} className="text-[11px] text-ink-3 hover:text-ink-1 inline-flex items-center gap-1 disabled:opacity-50">
                {carregandoAws ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} atualizar
              </button>
            </div>
            {!temCred ? (
              <div className="text-[11px] text-ink-4">Salve as credenciais pra consultar a conta.</div>
            ) : conta == null && identidades == null ? (
              <div className="text-[11px] text-ink-4">Carregando…</div>
            ) : (<>
              {conta && (
                <div className="flex items-center gap-2 flex-wrap mb-2 text-[11px]">
                  <StatusPill ok={conta.producao} warn label={conta.producao ? "produção" : "sandbox"} />
                  <StatusPill ok={conta.envio_habilitado} label={conta.envio_habilitado ? "envio habilitado" : "envio pausado"} />
                  <span className="inline-flex items-center gap-1 text-ink-3"><Gauge className="w-3 h-3" /> {conta.enviados_24h ?? 0}/{conta.cota_24h ?? "?"} nas últimas 24h · {conta.taxa_por_segundo ?? "?"}/s</span>
                </div>
              )}
              {conta && !conta.producao && (
                <div className="mb-2 p-2 rounded bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-100">
                  Conta em <b>sandbox</b>: só envia pra endereços verificados, 200/dia, 1/s. Peça acesso de produção no console do SES (Account dashboard → Request production access).
                </div>
              )}
              {identidades && identidades.length === 0 ? (
                <div className="text-[11px] text-amber-200">Nenhuma identidade na região {regiao}. Adicione o domínio em SES → Identities e crie os CNAMEs de DKIM no DNS.</div>
              ) : identidades && (
                <ul className="space-y-1.5">
                  {identidades.map((i) => (
                    <li key={i.nome} className="text-xs space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-ink-1 truncate">{i.nome} <span className="text-ink-4">({i.tipo})</span></span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <StatusPill ok={i.verificado} warn label={i.verificado ? "verificado" : "pendente"} />
                          {i.tipo === "dominio" && <StatusPill ok={i.dkim_status === "SUCCESS"} warn label={`DKIM ${String(i.dkim_status || "?").toLowerCase()}`} />}
                        </div>
                      </div>
                      {!i.verificado && i.registros_dkim?.length > 0 && (
                        <div className="ml-2 p-2 rounded bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-100 space-y-1">
                          <div>Crie estes CNAMEs no DNS de <b>{i.nome}</b>:</div>
                          {i.registros_dkim.map((r) => (
                            <div key={r.name} className="flex items-center gap-2 font-mono">
                              <span className="truncate">{r.name}</span><span className="text-ink-3">→</span><span className="truncate">{r.value}</span>
                              <button onClick={() => copiar(`${r.name} CNAME ${r.value}`)} className="p-1 rounded hover:bg-surface-2 text-ink-3" title="Copiar"><Copy className="w-3 h-3" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-2 text-[11px] text-ink-4">
                Abertos e cliques vêm do próprio SES (rastreio padrão do configuration set), sem CNAME extra. Apple Mail Privacy e clientes que bloqueiam imagens podem não contar.
              </div>
            </>)}
            {dominioRemetente && identidades && identidades.length > 0 && !remetenteVerificado && (
              <div className="mt-2 text-[11px] text-amber-200 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> O remetente ({remetenteEmail}) não está verificado nesta região — o SES vai recusar o envio.
              </div>
            )}
          </div>

          {/* EVENTOS (SNS) */}
          <div className="p-3 rounded-lg bg-surface-1 border border-white/[0.06] space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-ink-2 font-medium inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Eventos (entregue, aberto, clique, bounce, spam) via SNS</div>
              <StatusPill ok={snsConfirmado} warn label={snsConfirmado ? "endpoint confirmado" : "não confirmado"} />
            </div>
            {webhookUrl ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-[11px] text-ink-2 bg-base border border-white/[0.06] rounded px-2 py-1">{webhookUrl}</code>
                <button onClick={() => copiar(webhookUrl)} className="p-1.5 rounded hover:bg-surface-2 text-ink-3" title="Copiar"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div className="text-[11px] text-ink-4">Salve a configuração pra gerar a URL do endpoint.</div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <button
                onClick={provisionar}
                disabled={provisionando || (!temCred && !accessKeyId.trim())}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-200 text-xs hover:bg-amber-500/25 disabled:opacity-50"
              >
                {provisionando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                Ligar eventos automaticamente (config set + SNS)
              </button>
              <span className="text-[11px] text-ink-4">Precisa de permissão SES + SNS na access key.</span>
            </div>
            <details className="text-[11px] text-ink-4">
              <summary className="cursor-pointer hover:text-ink-2">Configurei manualmente — informar configuration set e tópico SNS</summary>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input value={configSet} onChange={(e) => setConfigSet(e.target.value)} placeholder="configuration set (ex.: metriqa-1)" className={`${inputCls} text-ink-1`} />
                <input value={topicArn} onChange={(e) => setTopicArn(e.target.value)} placeholder="arn:aws:sns:us-east-1:123456789012:metriqa-ses" className={`${inputCls} text-ink-1`} />
              </div>
              <div className="mt-1">No SES: configuration set → event destination tipo SNS com todos os eventos. No SNS: subscription HTTPS pra URL acima. O endpoint confirma sozinho.</div>
            </details>
          </div>

          {/* TESTE */}
          <div className="p-3 rounded-lg bg-surface-1 border border-white/[0.06]">
            <div className="text-xs text-ink-2 font-medium mb-2 inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Enviar e-mail de teste</div>
            <div className="flex gap-2">
              <input value={testePara} onChange={(e) => setTestePara(e.target.value)} placeholder="seu@email.com"
                className="flex-1 bg-base border border-white/[0.06] focus:border-emerald-500/40 rounded-lg px-3 py-2 text-sm outline-none" />
              <button
                onClick={testar}
                disabled={testando || !testePara.trim() || (!temCred && !accessKeyId.trim()) || !remetenteEmail}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-surface-2 border border-white/[0.08] text-ink-1 text-xs hover:bg-surface-3 disabled:opacity-50"
              >
                {testando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Testar
              </button>
            </div>
            {conta && !conta.producao && <div className="mt-1 text-[11px] text-ink-4">Em sandbox o destino também precisa estar verificado no SES.</div>}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-white/[0.04] flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-ink-3 hover:bg-surface-1">Fechar</button>
          <button onClick={salvar} disabled={salvando} className="btn-primary !py-1.5 !px-4 inline-flex items-center gap-2 disabled:opacity-60">
            {salvando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
